/**
 * Legacy bucketplace/ui에서 필요한 것들만 따로 옮겨두었습니다.
 * 새로만든 Sticky를 기획전에 적용할 수 있으면 제거하면 됩니다.
 */

import { useCallback } from 'react';
import { useContext } from 'use-context-selector';

import { StickyControlContext, StickyValueEntry } from '../contexts/internal/StickyContext';
import { StickyDirection } from '../types';

export function useGetStickyHeight(
  direction: StickyDirection = 'top',
): (
    targetY: number,
    filterEntry?: (entry: StickyValueEntry) => boolean,
  ) => number {
  const stickyControlContext = useContext(StickyControlContext);
  if (stickyControlContext == null) {
    throw new Error('StickyContext is not provided');
  }
  // TODO: This will be inaccurate if there are non-overlapping
  // sticky on the document. The difference will be minor if there aren't
  // too much of them, however, to correctly implement that, we basically have
  // to rewrite sticky emulation here. Because of this, I'm leaving this as-is,
  // yet, to allow future updates without breaking change, this accepts targetY
  // as the target position of the document. This can be implemented later,
  // and the upgrade path would be simple enough.
  return useCallback((
    targetY: number,
    filterEntry?: (entry: StickyValueEntry) => boolean,
  ): number => {
    const { entries } = stickyControlContext.getState();
    const entryList = Object.keys(entries)
      .map((key) => entries[key])
      .filter((entry) => {
        if (entry.direction !== direction) {
          return false;
        }
        if (!entry.shouldInfluenceScroll) {
          return false;
        }
        if (filterEntry != null && !filterEntry(entry)) {
          return false;
        }
        return true;
      });
    const height = entryList.reduce((prev, entry) => {
      if (entry.disabled) {
        return prev;
      }
      if (entry.beginY > targetY) {
        return prev;
      }
      const maxOffset =
        entry.offsetRange != null ? entry.offsetRange[1] : entry.offset;
      return prev + maxOffset + (entry.height || 0);
    }, 0);
    return height;
  }, [stickyControlContext, direction]);
}
